import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  resolveOrganizationId,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { readProperty } from '@/lib/utils'
import {
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type DefaultError,
} from '@tanstack/react-query'
import {
  cancelSnapdragonJob,
  connectSnapdragon,
  createSnapdragonSetupCode,
  dismissSnapdragonJob,
  disconnectSnapdragon,
  disconnectSnapdragonComputer,
  getSnapdragonHealth,
  getSnapdragonInstallers,
  getSnapdragonJob,
  getSnapdragonJobs,
  getSnapdragonPrintAttempts,
  getSnapdragonPrinters,
  getSnapdragonSalesOrderAutoPrintSettings,
  getSnapdragonWorkstation,
  getSnapdragonWorkstations,
  markSnapdragonJobPrinted,
  refreshSnapdragonPrinters,
  removeStaleSnapdragonPrinters,
  requeueSnapdragonJob,
  saveSnapdragonSalesOrderAutoPrintSettings,
  saveSnapdragonWorkstation,
  saveSnapdragonWorkstationPrintingSettings,
  sendSnapdragonTestPrint,
  type SnapdragonFilteredKeyEntries,
  type SnapdragonJobKeyEntries,
  type SnapdragonKeyEntries,
  type SnapdragonOrganizationScope,
  type SnapdragonWorkstationLookup,
} from './snapdragon.api'
import type {
  SnapdragonAutoPrintSettingsInput,
  SnapdragonHealth,
  SnapdragonJobFilters,
  SnapdragonPrintersResponse,
  SnapdragonTestPrintVariables,
  SnapdragonWorkstationInput,
  SnapdragonWorkstationPrintingSettingsInput,
  SnapdragonWorkstationsResponse,
} from './snapdragon.types'

type RequiredOrganizationId = SnapdragonOrganizationScope['organizationId']
type SetupCodeVariables = Pick<Parameters<typeof connectSnapdragon>[0], 'machineId'> | undefined

/**
 * A hook's organization: the explicit id, else the current route's `$orgId`. Without one, the root
 * key matches every organization.
 */
interface SnapdragonHookScope {
  organizationId?: OrganizationIdInput
}

/** The workstation query's key; the lookup may not know a machine id. */
interface SnapdragonWorkstationKeyEntries extends SnapdragonKeyEntries {
  computerId?: string
  machineId?: string | null
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type SnapdragonQueryKey<TEntries> = [AdminQueryRoot & TEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn' | 'enabled'>> & {
  enabled?: boolean
}

type SuspenseQueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn' | 'enabled'>>

export const snapdragonQueryKeys = {
  all: ({ organizationId }: SnapdragonHookScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'snapdragon' }, { organizationId }),
  ],
  printers: ({ organizationId }: SnapdragonKeyEntries): SnapdragonQueryKey<SnapdragonKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'printers',
    }),
  health: ({ organizationId }: SnapdragonKeyEntries): SnapdragonQueryKey<SnapdragonKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'health',
    }),
  installers: ({
    organizationId,
  }: SnapdragonKeyEntries): SnapdragonQueryKey<SnapdragonKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'installers',
    }),
  salesOrderAutoPrintSettings: ({
    organizationId,
  }: SnapdragonKeyEntries): SnapdragonQueryKey<SnapdragonKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'sales-order-auto-print-settings',
    }),
  workstations: ({
    organizationId,
  }: SnapdragonKeyEntries): SnapdragonQueryKey<SnapdragonKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'workstations',
    }),
  workstation: ({
    organizationId,
    computerId,
    machineId,
  }: SnapdragonWorkstationKeyEntries): SnapdragonQueryKey<SnapdragonWorkstationKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'workstation',
      computerId,
      machineId: machineId || undefined,
    }),
  jobs: ({
    organizationId,
    filters,
  }: SnapdragonFilteredKeyEntries): SnapdragonQueryKey<SnapdragonFilteredKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'jobs',
      filters,
    }),
  printAttempts: ({
    organizationId,
    filters,
  }: SnapdragonFilteredKeyEntries): SnapdragonQueryKey<SnapdragonFilteredKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'print-attempts',
      filters,
    }),
  job: ({
    organizationId,
    jobId,
  }: SnapdragonJobKeyEntries): SnapdragonQueryKey<SnapdragonJobKeyEntries> =>
    buildScopedAdminQueryKey(snapdragonQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'job',
      jobId,
    }),
}

function remoteRefetchOnWindowFocus(organizationId: RequiredOrganizationId): () => boolean {
  return () =>
    queryClient.getQueryData<SnapdragonHealth>(snapdragonQueryKeys.health({ organizationId }))
      ?.realtime_enabled !== true
}

/** A filtered list query's scope. */
interface SnapdragonFilteredScope extends SnapdragonOrganizationScope {
  filters?: SnapdragonJobFilters
}

/** A job query's scope; `useSnapdragonJob()` keeps it idle until it has a job id. */
interface SnapdragonJobScope extends SnapdragonOrganizationScope {
  jobId?: string | null
}

export const snapdragonQueries = {
  printers: ({ organizationId }: SnapdragonOrganizationScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.printers({ organizationId }),
      queryFn: getSnapdragonPrinters,
      refetchOnWindowFocus: remoteRefetchOnWindowFocus(organizationId),
    }),
  health: ({ organizationId }: SnapdragonOrganizationScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.health({ organizationId }),
      queryFn: getSnapdragonHealth,
    }),
  installers: ({ organizationId }: SnapdragonOrganizationScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.installers({ organizationId }),
      queryFn: getSnapdragonInstallers,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }),
  salesOrderAutoPrintSettings: ({ organizationId }: SnapdragonOrganizationScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.salesOrderAutoPrintSettings({ organizationId }),
      queryFn: getSnapdragonSalesOrderAutoPrintSettings,
    }),
  workstations: ({ organizationId }: SnapdragonOrganizationScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.workstations({ organizationId }),
      queryFn: getSnapdragonWorkstations,
    }),
  workstation: ({ organizationId, computerId, machineId }: SnapdragonWorkstationLookup) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.workstation({ organizationId, computerId, machineId }),
      queryFn: ({ signal }) =>
        getSnapdragonWorkstation({ organizationId, computerId, machineId, signal }),
      enabled: Boolean(computerId),
    }),
  jobs: ({ organizationId, filters }: SnapdragonFilteredScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.jobs({ organizationId, filters }),
      queryFn: getSnapdragonJobs,
      refetchOnWindowFocus: remoteRefetchOnWindowFocus(organizationId),
    }),
  printAttempts: ({ organizationId, filters }: SnapdragonFilteredScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.printAttempts({ organizationId, filters }),
      queryFn: getSnapdragonPrintAttempts,
    }),
  job: ({ organizationId, jobId }: SnapdragonJobScope) =>
    queryOptions({
      queryKey: snapdragonQueryKeys.job({ organizationId, jobId }),
      queryFn: getSnapdragonJob,
      refetchOnWindowFocus: remoteRefetchOnWindowFocus(organizationId),
    }),
}

export function useSuspenseSnapdragonPrinters({
  organizationId,
  ...params
}: SnapdragonHookScope & SuspenseQueryParams<ReturnType<typeof snapdragonQueries.printers>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...snapdragonQueries.printers({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSnapdragonPrinters({
  organizationId,
  enabled = true,
  ...params
}: SnapdragonHookScope & QueryParams<ReturnType<typeof snapdragonQueries.printers>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.printers({ organizationId: resolvedOrganizationId }),
    enabled,
    ...params,
  })
}

export function useSnapdragonHealth({
  organizationId,
  enabled = true,
  ...params
}: SnapdragonHookScope & QueryParams<ReturnType<typeof snapdragonQueries.health>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.health({ organizationId: resolvedOrganizationId }),
    enabled,
    ...params,
  })
}

export function useSnapdragonInstallers({
  organizationId,
  enabled = true,
  ...params
}: SnapdragonHookScope & QueryParams<ReturnType<typeof snapdragonQueries.installers>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.installers({ organizationId: resolvedOrganizationId }),
    enabled,
    ...params,
  })
}

export function useSuspenseSnapdragonSalesOrderAutoPrintSettings({
  organizationId,
  ...params
}: SnapdragonHookScope &
  SuspenseQueryParams<ReturnType<typeof snapdragonQueries.salesOrderAutoPrintSettings>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...snapdragonQueries.salesOrderAutoPrintSettings({
      organizationId: resolvedOrganizationId,
    }),
    ...params,
  })
}

export function useSnapdragonSalesOrderAutoPrintSettings({
  organizationId,
  enabled = true,
  ...params
}: SnapdragonHookScope &
  QueryParams<ReturnType<typeof snapdragonQueries.salesOrderAutoPrintSettings>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.salesOrderAutoPrintSettings({
      organizationId: resolvedOrganizationId,
    }),
    enabled,
    ...params,
  })
}

export function useSnapdragonWorkstations({
  organizationId,
  enabled = true,
  ...params
}: SnapdragonHookScope & QueryParams<ReturnType<typeof snapdragonQueries.workstations>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.workstations({ organizationId: resolvedOrganizationId }),
    enabled,
    ...params,
  })
}

/** This browser's saved workstation; idle while the browser has no computer id yet. */
export function useSnapdragonWorkstation({
  organizationId,
  computerId,
  machineId,
  enabled = true,
  ...params
}: SnapdragonHookScope &
  Omit<SnapdragonWorkstationLookup, 'organizationId'> &
  QueryParams<ReturnType<typeof snapdragonQueries.workstation>>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.workstation({
      organizationId: resolvedOrganizationId,
      computerId,
      machineId,
    }),
    enabled: enabled && Boolean(computerId),
    ...params,
  })
}

export function useSnapdragonJobs({
  organizationId,
  filters,
  enabled = true,
  ...params
}: SnapdragonHookScope & {
  filters?: SnapdragonJobFilters
} & QueryParams<ReturnType<typeof snapdragonQueries.jobs>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.jobs({ organizationId: resolvedOrganizationId, filters }),
    enabled,
    ...params,
  })
}

export function useSnapdragonPrintAttempts({
  organizationId,
  filters,
  enabled = true,
  ...params
}: SnapdragonHookScope & {
  filters?: SnapdragonJobFilters
} & QueryParams<ReturnType<typeof snapdragonQueries.printAttempts>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.printAttempts({ organizationId: resolvedOrganizationId, filters }),
    enabled,
    ...params,
  })
}

/** One job's timeline; idle until a job is selected. */
export function useSnapdragonJob({
  organizationId,
  jobId,
  enabled = true,
  ...params
}: SnapdragonHookScope & {
  jobId?: string | null
} & QueryParams<ReturnType<typeof snapdragonQueries.job>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...snapdragonQueries.job({ organizationId: resolvedOrganizationId, jobId }),
    enabled: enabled && Boolean(jobId),
    ...params,
  })
}

function invalidateSnapdragonSettings(organizationId: RequiredOrganizationId) {
  return queryClient.invalidateQueries({
    queryKey: snapdragonQueryKeys.salesOrderAutoPrintSettings({ organizationId }),
  })
}

function invalidateSnapdragonPrinters(organizationId: RequiredOrganizationId) {
  return queryClient.invalidateQueries({
    queryKey: snapdragonQueryKeys.printers({ organizationId }),
  })
}

function invalidateSnapdragonHealth(organizationId: RequiredOrganizationId) {
  return queryClient.invalidateQueries({
    queryKey: snapdragonQueryKeys.health({ organizationId }),
  })
}

export function invalidateSnapdragonWorkstations(organizationId: RequiredOrganizationId) {
  return queryClient.invalidateQueries({
    queryKey: snapdragonQueryKeys.workstations({ organizationId }),
  })
}

function invalidateSnapdragonPrintActivity(organizationId: RequiredOrganizationId) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: snapdragonQueryKeys.jobs({ organizationId }),
    }),
    queryClient.invalidateQueries({
      queryKey: snapdragonQueryKeys.job({ organizationId }),
    }),
    queryClient.invalidateQueries({
      queryKey: snapdragonQueryKeys.printAttempts({ organizationId }),
    }),
  ])
}

function invalidateSnapdragonConnectionState(
  organizationId: RequiredOrganizationId,
  { includeSettings = true } = {}
) {
  const invalidations = [
    invalidateSnapdragonPrinters(organizationId),
    invalidateSnapdragonHealth(organizationId),
  ]

  if (includeSettings) {
    invalidations.push(invalidateSnapdragonSettings(organizationId))
  }

  return Promise.all(invalidations)
}

function updateSnapdragonPrinters(
  organizationId: RequiredOrganizationId,
  response: SnapdragonPrintersResponse
) {
  queryClient.setQueryData(snapdragonQueryKeys.printers({ organizationId }), response)
}

export function useConnectSnapdragonMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation<
    Awaited<ReturnType<typeof connectSnapdragon>>,
    DefaultError,
    SetupCodeVariables
  >({
    mutationFn: ({ machineId }: { machineId?: string | null } = {}) =>
      connectSnapdragon({ organizationId, machineId }),
    onSuccess: () => invalidateSnapdragonConnectionState(organizationId),
  })
}

export function useCreateSnapdragonSetupCodeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation<
    Awaited<ReturnType<typeof createSnapdragonSetupCode>>,
    DefaultError,
    SetupCodeVariables
  >({
    mutationFn: ({ machineId }: { machineId?: string | null } = {}) =>
      createSnapdragonSetupCode({ organizationId, machineId }),
    onSuccess: () =>
      invalidateSnapdragonConnectionState(organizationId, { includeSettings: false }),
  })
}

export function useDisconnectSnapdragonMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => disconnectSnapdragon({ organizationId }),
    onSuccess: () => invalidateSnapdragonConnectionState(organizationId),
  })
}

/** Disconnects a computer of the given organization, or of the route's when none is given. */
export function useDisconnectSnapdragonComputerMutation() {
  const routeOrganizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      organizationId,
      stationId,
    }: {
      organizationId?: OrganizationIdInput
      stationId: string
    }) =>
      disconnectSnapdragonComputer({
        organizationId: resolveOrganizationId(organizationId, routeOrganizationId),
        stationId,
      }),
    onSuccess: (_response, { organizationId }) =>
      invalidateSnapdragonConnectionState(
        resolveOrganizationId(organizationId, routeOrganizationId)
      ),
  })
}

export function useSaveSnapdragonWorkstationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (workstation: SnapdragonWorkstationInput) =>
      saveSnapdragonWorkstation({ organizationId, ...workstation }),
    onSuccess: async (response) => {
      const computerId = response?.workstation?.computer_id
      const machineId = response?.workstation?.machine_id
      if (computerId) {
        const queryKey = snapdragonQueryKeys.workstation({ organizationId, computerId, machineId })
        await queryClient.cancelQueries({ queryKey, exact: true })
        queryClient.setQueryData(queryKey, response)
      }

      return invalidateSnapdragonWorkstations(organizationId)
    },
  })
}

export function useSaveSnapdragonWorkstationPrintingSettingsMutation({
  organizationId,
}: SnapdragonHookScope = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useMutation({
    mutationFn: (
      settings: SnapdragonWorkstationPrintingSettingsInput & { workstationId: number }
    ) =>
      saveSnapdragonWorkstationPrintingSettings({
        ...settings,
        organizationId: resolvedOrganizationId,
      }),
    onSuccess: async ({ workstation }) => {
      const queryKey = snapdragonQueryKeys.workstations({ organizationId: resolvedOrganizationId })
      await queryClient.cancelQueries({ queryKey, exact: true })
      queryClient.setQueryData<SnapdragonWorkstationsResponse>(
        queryKey,
        (current) =>
          current && {
            ...current,
            workstations: current.workstations.map((saved) =>
              saved.id === workstation.id ? workstation : saved
            ),
          }
      )
      return queryClient.invalidateQueries({
        queryKey: snapdragonQueryKeys.all({ organizationId: resolvedOrganizationId }),
        // Only this workstation's detail queries; their data is `{ workstation }`.
        predicate: (query) =>
          readProperty(readProperty(query.state.data, 'workstation'), 'id') === workstation.id,
      })
    },
  })
}

export function useRefreshSnapdragonPrintersMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => refreshSnapdragonPrinters({ organizationId }),
    onSuccess: (response) => {
      updateSnapdragonPrinters(organizationId, response)

      return Promise.all([
        invalidateSnapdragonSettings(organizationId),
        invalidateSnapdragonHealth(organizationId),
        invalidateSnapdragonPrintActivity(organizationId),
      ])
    },
  })
}

export function useRemoveStaleSnapdragonPrintersMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => removeStaleSnapdragonPrinters({ organizationId }),
    onSuccess: (response) => updateSnapdragonPrinters(organizationId, response),
  })
}

export function useSaveSnapdragonSalesOrderAutoPrintSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (settings: SnapdragonAutoPrintSettingsInput) =>
      saveSnapdragonSalesOrderAutoPrintSettings({ organizationId, settings }),
    onSuccess: () =>
      Promise.all([
        invalidateSnapdragonSettings(organizationId),
        invalidateSnapdragonHealth(organizationId),
        invalidateSnapdragonPrinters(organizationId),
      ]),
  })
}

export function useSendSnapdragonTestPrintMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ printerId, template, tray, rotate180 }: SnapdragonTestPrintVariables = {}) =>
      sendSnapdragonTestPrint({ organizationId, printerId, template, tray, rotate180 }),
    onSuccess: () =>
      Promise.all([
        invalidateSnapdragonPrintActivity(organizationId),
        invalidateSnapdragonHealth(organizationId),
      ]),
  })
}

/** Print job actions take the Snapdragon job id or a local attempt's `flowers-print-attempt:` id. */
interface SnapdragonJobVariables {
  jobId: string
}

export function useRequeueSnapdragonJobMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ jobId }: SnapdragonJobVariables) =>
      requeueSnapdragonJob({ organizationId, jobId }),
    onSuccess: () =>
      Promise.all([
        invalidateSnapdragonPrintActivity(organizationId),
        invalidateSnapdragonHealth(organizationId),
      ]),
  })
}

export function useMarkSnapdragonJobPrintedMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ jobId }: SnapdragonJobVariables) =>
      markSnapdragonJobPrinted({ organizationId, jobId }),
    onSuccess: () =>
      Promise.all([
        invalidateSnapdragonPrintActivity(organizationId),
        invalidateSnapdragonHealth(organizationId),
      ]),
  })
}

export function useCancelSnapdragonJobMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ jobId }: SnapdragonJobVariables) =>
      cancelSnapdragonJob({ organizationId, jobId }),
    onSuccess: () =>
      Promise.all([
        invalidateSnapdragonPrintActivity(organizationId),
        invalidateSnapdragonHealth(organizationId),
      ]),
  })
}

export function useDismissSnapdragonJobMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ jobId }: SnapdragonJobVariables) =>
      dismissSnapdragonJob({ organizationId, jobId }),
    onSuccess: () =>
      Promise.all([
        invalidateSnapdragonPrintActivity(organizationId),
        invalidateSnapdragonHealth(organizationId),
      ]),
  })
}
